import React from 'react';

/** Tag 类型：positive=正面标签（绿色），negative=负面标签（红色） */
export type TagType = 'positive' | 'negative';

interface TagProps {
  /** 标签文本 */
  label: string;
  /** 标签类型：positive=正面（绿色），negative=负面（红色） */
  type?: TagType;
  /** 是否选中（深色 + 右上角√） */
  active?: boolean;
  /** 点击回调 */
  onClick?: () => void;
  /** 尺寸 */
  size?: 'sm' | 'md';
  /** 自定义类名 */
  className?: string;
  /** 不显示 # 前缀 */
  noHash?: boolean;
}

export const Tag: React.FC<TagProps> = ({
  label,
  type = 'positive',
  active = false,
  onClick,
  size = 'md',
  className = '',
  noHash = false,
}) => {
  // 尺寸配置
  const sizeConfig = {
    sm: {
      classes: 'px-2 py-0.5 text-[10px]',
      shadowOffset: 2,
      checkSize: 14,
      checkFont: 9,
      checkTop: 4, // 小号 √ 下移
    },
    md: {
      classes: 'px-3 py-1 text-xs',
      shadowOffset: 3,
      checkSize: 18,
      checkFont: 11,
      checkTop: -5, // 中号 √ 下移
    },
  };

  const config = sizeConfig[size];

  // 颜色配置
  const colorConfig = {
    positive: {
      bg: active ? '#2E8B57' : '#90EE90',
      border: active ? '#1E6B47' : '#5CB85C',
      text: active ? '#ffffff' : '#228B22',
      shadowLight: 'rgba(255,255,255,0.5)',
      shadowDark: active ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)',
    },
    negative: {
      bg: active ? '#B22222' : '#FFB3B3',
      border: active ? '#8B0000' : '#E57373',
      text: active ? '#ffffff' : '#B22222',
      shadowLight: 'rgba(255,255,255,0.5)',
      shadowDark: active ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)',
    },
  };

  const color = colorConfig[type];

  // √ 水平位置：中心对齐到标签右边框
  const checkRight = -config.checkSize / 2;

  // 锯齿 clip-path 定义
  const clipPathSm = 'polygon(0 4px, 2px 4px, 2px 2px, 4px 2px, 4px 0, calc(100% - 4px) 0, calc(100% - 4px) 2px, calc(100% - 2px) 2px, calc(100% - 2px) 4px, 100% 4px, 100% calc(100% - 4px), calc(100% - 2px) calc(100% - 4px), calc(100% - 2px) calc(100% - 2px), calc(100% - 4px) calc(100% - 2px), calc(100% - 4px) 100%, 4px 100%, 4px calc(100% - 2px), 2px calc(100% - 2px), 2px calc(100% - 4px), 0 calc(100% - 4px))';
  const clipPathMd = 'polygon(0 6px, 3px 6px, 3px 3px, 6px 3px, 6px 0, calc(100% - 6px) 0, calc(100% - 6px) 3px, calc(100% - 3px) 3px, calc(100% - 3px) 6px, 100% 6px, 100% calc(100% - 6px), calc(100% - 3px) calc(100% - 6px), calc(100% - 3px) calc(100% - 3px), calc(100% - 6px) calc(100% - 3px), calc(100% - 6px) 100%, 6px 100%, 6px calc(100% - 3px), 3px calc(100% - 3px), 3px calc(100% - 6px), 0 calc(100% - 6px))';

  return (
    // 外层包装器 - 不受 clip-path 裁剪
    <span
      className={`relative inline-block cursor-pointer select-none ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* 主标签容器 - 带锯齿 */}
      <span
        className={`
          inline-flex items-center justify-center
          font-bold uppercase tracking-wide
          transition-all duration-75
          hover:brightness-110
          active:translate-y-[1px]
          ${config.classes}
        `}
        style={{
          backgroundColor: color.bg,
          color: color.text,
          clipPath: size === 'sm' ? clipPathSm : clipPathMd,
          boxShadow: `inset -${config.shadowOffset}px -${config.shadowOffset}px 0px 0px ${color.shadowDark}, inset ${config.shadowOffset}px ${config.shadowOffset}px 0px 0px ${color.shadowLight}`,
        }}
      >
        {/* 边框层 */}
        <span
          className="absolute inset-0 -z-10"
          style={{
            boxShadow: `0 0 0 2px ${color.border}`,
            clipPath: size === 'sm' ? clipPathSm : clipPathMd,
          }}
        />
        {noHash ? label : `#${label}`}
      </span>

      {/* √ 图标 - 在外层容器中，中心对齐到标签右上角 */}
      {active && (
        <span
          className="absolute flex items-center justify-center text-white leading-none font-bold"
          style={{
            backgroundColor: '#FF8C00',
            width: `${config.checkSize}px`,
            height: `${config.checkSize}px`,
            fontSize: `${config.checkFont}px`,
            // 使用配置的 top 值，水平中心对齐
            top: `${config.checkTop}px`,
            right: `${checkRight}px`,
            clipPath: size === 'sm' ? clipPathSm : clipPathMd,
            boxShadow: 'inset -1px -1px 0px 0px rgba(0,0,0,0.3), inset 1px 1px 0px 0px rgba(255,255,255,0.3)',
          }}
        >
          ✓
        </span>
      )}
    </span>
  );
};

Tag.displayName = 'Tag';